import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Phone } from "lucide-react";
import { PageHead } from "@/components/utils";
import { FadeUp, ClipImage } from "@/components/Reveal";
import { BUSINESS, IMAGES, CATEGORIES } from "@/lib/data";
import { IDS } from "@/lib/testIds";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_SLOTS = [
  "10:00", "11:00", "12:00", "13:00", "14:00",
  "15:00", "16:00", "17:00", "18:00", "19:00",
];

const ALL_SERVICES = CATEGORIES.flatMap((c) =>
  c.items.map((i) => ({ label: `${c.title} · ${i.name}`, value: `${c.title} — ${i.name}` }))
);

export default function Book() {
  const today = new Date().toISOString().split("T")[0];
  const [form, setForm] = useState({
    name: "", phone: "", email: "", service: "", date: "", time: "", notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e && e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.service || !form.date || !form.time) {
      toast.error("Please fill in name, phone, service, date and time.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/appointments`, form);
      setDone(true);
      toast.success("Appointment requested — we'll confirm shortly.");
      setForm({ name: "", phone: "", email: "", service: "", date: "", time: "", notes: "" });
    } catch (err) {
      toast.error("Something went wrong. Please call the salon.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHead
        title="Book Appointment — Good Looks Unisex Salon"
        description="Book your appointment at Good Looks Unisex Salon in Sunni Gali, Sadar, Mansa."
      />
      <section className="bg-[#1B1B1B] text-[#F3EAD9] pt-40 pb-16">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10">
          <FadeUp>
            <p className="eyebrow text-[#C89B3C] mb-6">— Book appointment</p>
            <h1 className="font-display text-5xl md:text-8xl leading-[0.95] max-w-5xl">
              Reserve your<br /><span className="italic text-[#C89B3C]">seat in the chair.</span>
            </h1>
            <p className="mt-6 text-white/70 max-w-xl leading-relaxed">
              Tell us what you'd like done and when. We'll confirm your slot
              over a call or WhatsApp within working hours.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="bg-[#1B1B1B] text-[#F3EAD9] pb-24 md:pb-32">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <FadeUp className="lg:col-span-5 order-2 lg:order-1">
            <div className="border border-white/10 bg-black/20">
              <ClipImage src={IMAGES.contact} alt="Salon chair" className="aspect-[4/5]" />
            </div>
            <div className="mt-8 space-y-3 text-sm text-white/70">
              <p>Prefer a call?</p>
              <a href={BUSINESS.phoneHref} className="btn-ghost">
                <Phone size={14} /> <span>{BUSINESS.phone}</span>
              </a>
            </div>
          </FadeUp>

          <FadeUp className="lg:col-span-7 order-1 lg:order-2" delay={0.05}>
            <form
              onSubmit={submit}
              data-testid={IDS.bookForm}
              className="border border-white/10 bg-[#232323] p-8 md:p-10 space-y-6"
            >
              <div className="flex items-baseline justify-between">
                <h2 className="font-display text-3xl">Your details</h2>
                <span className="chapter-number">01</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="eyebrow text-white/60">Full name *</Label>
                  <Input
                    data-testid={IDS.bookName}
                    required
                    value={form.name}
                    onChange={update("name")}
                    className="mt-2 h-12 rounded-none border-x-0 border-t-0 border-b border-white/25 focus:border-[#C89B3C] px-0 bg-transparent text-white"
                  />
                </div>
                <div>
                  <Label className="eyebrow text-white/60">Phone *</Label>
                  <Input
                    data-testid={IDS.bookPhone}
                    required
                    value={form.phone}
                    onChange={update("phone")}
                    className="mt-2 h-12 rounded-none border-x-0 border-t-0 border-b border-white/25 focus:border-[#C89B3C] px-0 bg-transparent text-white"
                  />
                </div>
              </div>

              <div>
                <Label className="eyebrow text-white/60">Email</Label>
                <Input
                  data-testid={IDS.bookEmail}
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  className="mt-2 h-12 rounded-none border-x-0 border-t-0 border-b border-white/25 focus:border-[#C89B3C] px-0 bg-transparent text-white"
                />
              </div>

              <div className="pt-6 border-t border-white/10">
                <div className="flex items-baseline justify-between mb-6">
                  <h3 className="font-display text-3xl">The visit</h3>
                  <span className="chapter-number">02</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <Label className="eyebrow text-white/60">Service *</Label>
                    <Select value={form.service} onValueChange={update("service")}>
                      <SelectTrigger
                        data-testid={IDS.bookService}
                        className="mt-2 h-12 rounded-none border-x-0 border-t-0 border-b border-white/25 focus:border-[#C89B3C] px-0 bg-transparent text-white"
                      >
                        <SelectValue placeholder="Choose a service" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1B1B1B] text-white border-white/10 max-h-80">
                        {ALL_SERVICES.map((s) => (
                          <SelectItem key={s.value} value={s.value} className="focus:bg-[#C89B3C] focus:text-black">
                            {s.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="eyebrow text-white/60">Preferred date *</Label>
                    <Input
                      data-testid={IDS.bookDate}
                      type="date"
                      min={today}
                      required
                      value={form.date}
                      onChange={update("date")}
                      className="mt-2 h-12 rounded-none border-x-0 border-t-0 border-b border-white/25 focus:border-[#C89B3C] px-0 bg-transparent text-white [color-scheme:dark]"
                    />
                  </div>
                  <div>
                    <Label className="eyebrow text-white/60">Time slot *</Label>
                    <Select value={form.time} onValueChange={update("time")}>
                      <SelectTrigger
                        data-testid={IDS.bookTime}
                        className="mt-2 h-12 rounded-none border-x-0 border-t-0 border-b border-white/25 focus:border-[#C89B3C] px-0 bg-transparent text-white"
                      >
                        <SelectValue placeholder="Choose a time" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1B1B1B] text-white border-white/10">
                        {TIME_SLOTS.map((t) => (
                          <SelectItem key={t} value={t} className="focus:bg-[#C89B3C] focus:text-black">
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="mt-6">
                  <Label className="eyebrow text-white/60">Notes (optional)</Label>
                  <Textarea
                    data-testid={IDS.bookNotes}
                    rows={4}
                    value={form.notes}
                    onChange={update("notes")}
                    placeholder="Anything we should know — hair length, event date, allergies…"
                    className="mt-2 rounded-none border-x-0 border-t-0 border-b border-white/25 focus:border-[#C89B3C] px-0 bg-transparent text-white resize-none placeholder:text-white/30"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between pt-6">
                {done ? (
                  <p className="text-sm text-[#C89B3C]">Request sent — check your phone for confirmation.</p>
                ) : (
                  <p className="text-xs text-white/50">All slots confirmed by call/WhatsApp.</p>
                )}
                <button
                  type="submit"
                  data-testid={IDS.bookSubmit}
                  disabled={loading}
                  className="btn-gold disabled:opacity-60"
                >
                  <span>{loading ? "Sending…" : "Request appointment"}</span>
                  <ArrowUpRight size={16} />
                </button>
              </div>
            </form>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
